import React from 'react';
import { Link, useLocation } from 'react-router-dom';

export interface NavLinkProps {
  readonly to: string;
  readonly label: string;
  readonly icon?: React.ReactNode;
  readonly isMobile?: boolean;
  readonly matchDescendants?: boolean;
}

export const NavLink: React.FC<NavLinkProps> = ({
  to,
  label,
  icon,
  isMobile,
  matchDescendants = false,
}) => {
  const location = useLocation();
  const isActive =
    location.pathname === to ||
    (matchDescendants && to !== '/' && location.pathname.startsWith(`${to}/`));

  if (isMobile) {
    return (
      <Link
        to={to}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight: 'var(--min-touch-target)',
          padding: '6px 0 4px 0',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
          fontWeight: isActive ? 700 : 500,
          fontSize: '0.725rem',
          textDecoration: 'none',
          gap: '3px',
          position: 'relative',
          transition: 'color 0.15s ease',
        }}
        aria-current={isActive ? 'page' : undefined}
      >
        {icon && (
          <span
            style={{
              fontSize: '1.25rem',
              lineHeight: 1,
              transform: isActive ? 'scale(1.08)' : 'scale(1)',
              transition: 'transform 0.15s ease',
            }}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        <span style={{ letterSpacing: '-0.01em' }}>{label}</span>
        {isActive && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              width: '16px',
              height: '2px',
              borderRadius: '1px',
              backgroundColor: 'var(--color-accent)',
            }}
            aria-hidden="true"
          />
        )}
      </Link>
    );
  }

  return (
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        minHeight: 'var(--min-touch-target)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: isActive ? 'var(--color-accent-bg)' : 'transparent',
        color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
        fontWeight: isActive ? 700 : 500,
        fontSize: '0.9rem',
        textDecoration: 'none',
        border: isActive ? '1px solid var(--color-accent-border)' : '1px solid transparent',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      aria-current={isActive ? 'page' : undefined}
    >
      {icon && (
        <span style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden="true">
          {icon}
        </span>
      )}
      <span>{label}</span>
    </Link>
  );
};
